import {
  CompositeFilterDescriptor,
  DataResult,
  FilterOperator,
  State,
} from '@progress/kendo-data-query';
import { produce } from 'immer';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridCellProps,
  GridCustomCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import {
  getErrorMessage,
  hasValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import Checkbox from '../../../../../../common/widgets/Checkbox';
import { DataLoader } from '../../../../../../common/widgets/DataLoader';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import ExpandablePanel from '../../../../../../common/widgets/ExpandablePanel';
import Grid, { GridCellTd } from '../../../../../../common/widgets/Grid';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import { config } from '../../../../config';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { EmployeeTimesheetModel } from '../../../../models/EmployeeTimesheetModel';
import {
  ErrorState,
  ErrorType,
  ValidationError,
} from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { companyService } from '../../../../services/company';
import { timesheetsAdminService } from '../../../../services/timesheets-admin';
import { formatSelectedMonthYear } from '../../../MyAccount/TimeAndAttendencePage/Common';
import { emailCell } from '../../EmployeesAdmin/EmployeesAdminPage/Components/CustomCells';
import { ListItem } from '../../types';
import {
  EmployeeStateToBoolean,
  SearchCrtIdToFieldName,
  SearchFieldNames,
} from '../helper';
import ApproveTimesheetDialog from './Components/ApproveTimesheetDialog';
import {
  ActionsCell,
  ApprovedCell,
  SubmittedCell,
  companyNameCell,
  employeeFullNameCell,
} from './Components/CustomCells';
import { TimesheetSummaryDialog } from './Components/TimesheetSummaryDialog';

export interface TimesheetsAdminState {
  employees: {
    data: EmployeeTimesheetModel[];
    dataState: State;
    currentMonth: Moment;
    total: number;
  };
}

export default function TimesheetsAdminPage() {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const { isMobile, isTablet } = useBreakpoints();

  const isSubmittedItems: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.timesheets.filters.submitted),
    },
    {
      id: 1,
      name: translate(resources.admin.timesheets.filters.notSubmitted),
    },
  ];
  const isApprovedItems: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.timesheets.filters.approved),
    },
    {
      id: 1,
      name: translate(resources.admin.timesheets.filters.notApproved),
    },
  ];

  const companyDefaultItem: ListItem = {
    id: 0,
    name: translate(resources.admin.timesheets.filters.companyDefault),
  };
  const searchCrtItems: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.timesheets.filters.searchDefault),
    },
    {
      id: 1,
      name: translate(resources.admin.timesheets.grid.columns.name),
    },
    {
      id: 2,
      name: translate(resources.admin.employees.details.general.cnp),
    },
    {
      id: 3,
      name: translate(resources.admin.timesheets.grid.columns.eID),
    },
    {
      id: 4,
      name: translate(resources.admin.employees.grid.columns.email),
    },
  ];
  const initialDataState: State = {
    skip: 0,
    take: config.grid.defaultPageSize,
    sort: [{ field: 'employeeFullName', dir: 'asc' }],
    filter: {
      logic: 'and',
      filters: [
        {
          field: 'isSubmitted',
          operator: FilterOperator.EqualTo,
          value: EmployeeStateToBoolean[isSubmittedItems[0].id],
        },
        {
          field: 'isApproved',
          operator: FilterOperator.EqualTo,
          value: EmployeeStateToBoolean[isApprovedItems[0].id],
        },
      ],
    },
  };
  const [state, setState] = useState<TimesheetsAdminState>({
    employees: {
      data: [] as EmployeeTimesheetModel[],
      dataState: initialDataState,
      currentMonth: moment(),
    },
  } as TimesheetsAdminState);
  const [companyItems, setCompanyItems] = useState<ListItem[]>([]);
  const [companyFilter, setCompanyFilter] =
    useState<ListItem>(companyDefaultItem);
  const [isSubmittedFilter, setIsSubmittedFilter] = useState<ListItem>(
    isSubmittedItems[0],
  );
  const [isApprovedFilter, setIsApprovedFilter] = useState<ListItem>(
    isApprovedItems[0],
  );
  const [currentMonthFilter, setCurrentMonthFilter] = useState<Moment>(
    moment(),
  );

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchCrtFilter, setSearchCrtFilter] = useState<ListItem>(
    searchCrtItems[0],
  );
  // const [isApproving, setIsApproving] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);

  const getFilter = useCallback((): CompositeFilterDescriptor => {
    const filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
    if (companyFilter.id !== 0)
      filter.filters.push({
        field: 'companyId',
        operator: FilterOperator.EqualTo,
        value: companyFilter.id,
      });

    filter.filters.push({
      field: 'isSubmitted',
      operator: FilterOperator.EqualTo,
      value: EmployeeStateToBoolean[isSubmittedFilter.id],
    });
    filter.filters.push({
      field: 'isApproved',
      operator: FilterOperator.EqualTo,
      value: EmployeeStateToBoolean[isApprovedFilter.id],
    });
    if (!_.isEmpty(searchFilter)) {
      if (searchCrtFilter.id === 0) {
        const filters = SearchFieldNames.map((s: string) => {
          return {
            field: s,
            operator: FilterOperator.Contains,
            value: searchFilter.toLowerCase(),
            ignoreCase: true,
          };
        });
        filter.filters.push({ logic: 'or', filters });
      } else {
        filter.filters.push({
          field: SearchCrtIdToFieldName[searchCrtFilter.id],
          operator: FilterOperator.Contains,
          value: searchFilter.toLowerCase(),
          ignoreCase: true,
        });
      }
    }
    return filter;
  }, [
    companyFilter.id,
    searchCrtFilter.id,
    searchFilter,
    isSubmittedFilter.id,
    isApprovedFilter.id,
  ]);
  const [reloadData, setReloadData] = useState<boolean>(false);

  const [showTimesheetSummaryDialog, setShowTimesheetSummaryDialog] =
    useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [timesheetSummaryEmployeeId, setTimesheetSummaryEmployeeId] =
    useState<number>();
  const [timesheetSummaryEmployeeName, setTimesheetSummaryEmployeeName] =
    useState<string>();

  const setFilter = useCallback(() => {
    const filter = getFilter();
    setState(
      produce(d => {
        d.employees.dataState.filter = filter;
        d.employees.currentMonth = currentMonthFilter;
      }),
    );
  }, [currentMonthFilter, getFilter]);

  const onCampanyFilterChanged = (e: DropDownListChangeEvent) => {
    const company = e.value as ListItem;
    setCompanyFilter(company);
  };

  const onSearchCrtFilterChanged = (e: DropDownListChangeEvent) => {
    const searchCrt = e.value as ListItem;
    setSearchCrtFilter(searchCrt);
  };

  const onSearchFilterChange = (e: InputChangeEvent) => {
    const search = e.value;
    setSearchFilter(search);
  };

  const onIsSubmittedFilterChanged = (e: DropDownListChangeEvent) => {
    const state = e.value as ListItem;
    setIsSubmittedFilter(state);
  };

  const onIsApprovedFilterChanged = (e: DropDownListChangeEvent) => {
    const state = e.value as ListItem;
    setIsApprovedFilter(state);
  };

  const onDataReceived = (result: DataResult) => {
    setReloadData(false);

    setState(
      produce(draft => {
        draft.employees.data = result.data;
        draft.employees.total = result.total;
      }),
    );
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    clearSelectedRows();

    setState(
      produce(draft => {
        draft.employees.dataState = e.dataState;
      }),
    );
  };
  const handleResendEmail = async (employeeId: number) => {
    const response = await timesheetsAdminService.sendRemiderEmail(employeeId);
    if (response.hasErrors) {
      showErrorToast(translate(resources.admin.timesheets.error));
    } else {
      showSuccessToast(translate(resources.admin.timesheets.approve.success));
    }
  };

  const handleSeeSummary = async (employeeId: number, employeeName: string) => {
    setShowTimesheetSummaryDialog(true);
    setTimesheetSummaryEmployeeId(employeeId);
    setTimesheetSummaryEmployeeName(employeeName);
  };

  const loadOtherData = (abortController: AbortController | undefined) => {
    (async () => {
      const result = await companyService.getCompaniesByClient(
        '?$select=CompanyId, Name',
        {
          signal: abortController?.signal,
        },
      );

      if (!result.hasErrors) {
        setCompanyItems(
          result.data.map(c => {
            return { id: c.companyId, name: c.name };
          }) as ListItem[],
        );
      }
    })();
  };
  const ActionsCellCustomCell = (props: GridCustomCellProps) => (
    <ActionsCell
      {...props}
      handleResendEmail={handleResendEmail}
      handleSeeSummary={handleSeeSummary}
      selectedMonth={currentMonthFilter}
    />
  );

  const [selectedRows, setSelectedRows] = useState(new Set<number>());

  const toggleSelectRow = (employeeId: number) => {
    const newSet = new Set(selectedRows);
    if (newSet.has(employeeId)) {
      newSet.delete(employeeId);
    } else {
      newSet.add(employeeId);
    }
    setSelectedRows(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === state.employees.data.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(state.employees.data.map(e => e.employeeId)));
    }
  };

  const clearSelectedRows = () => {
    setSelectedRows(new Set());
  };

  const handleApprove = async () => {
    setIsApproving(true);
    const result = await timesheetsAdminService.approveTimesheets(
      Array.from(selectedRows),
      currentMonthFilter,
    );

    if (!result.hasErrors) {
      clearSelectedRows();
      setReloadData(true);
    } else {
      showErrorToast(translate(resources.admin.timesheets.error));
    }
    setShowApproveDialog(false);
    setIsApproving(false);
  };

  const handleReopen = async () => {
    const result = await timesheetsAdminService.reopenTimesheets(
      Array.from(selectedRows),
      currentMonthFilter,
    );

    if (!result.hasErrors) {
      clearSelectedRows();
      setReloadData(true);
      showSuccessToast(translate(resources.admin.timesheets.reopen.success));
    } else {
      showErrorToast(translate(resources.admin.timesheets.error));
    }
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    const resp = await timesheetsAdminService.downloadAttendanceReport(
      currentMonthFilter.year(),
      currentMonthFilter.month() + 1,
      Array.from(selectedRows.values()),
      `Timesheet ${
        currentMonthFilter.month() + 1
      } ${currentMonthFilter.year()}`,
    );
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        const validationErrors: ValidationError[] = [] as ValidationError[];
        validationErrors.push({
          code: 'noTimesheet',
          message: translate(resources.admin.timesheets.errors.noTimesheet),
          severity: 'error',
          placeholders: {},
        } as ValidationError);
        setErrors(
          produce(d => {
            d['null'] = validationErrors;
          }),
        );
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    }
    setIsGeneratingReport(false);
  };

  const clearErrors = (field: keyof typeof errors) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  useEffect(() => {
    clearSelectedRows();
    setFilter();
  }, [
    companyFilter,
    isSubmittedFilter,
    isApprovedFilter,
    searchCrtFilter,
    searchFilter,
    currentMonthFilter,
    setFilter,
  ]);

  useMount(abortController => {
    loadOtherData(abortController);
  });

  const selectCheckboxCell = ({ dataItem }: GridCellProps) => (
    <GridCellTd className="grid-check-cell">
      <Checkbox
        label={undefined}
        isChecked={selectedRows.has(dataItem.employeeId)}
        onChange={() => toggleSelectRow(dataItem.employeeId)}
      />
    </GridCellTd>
  );

  return (
    <>
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Error}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: undefined,
          }))}
          showClose
        />
      )}
      <div className="panel padding-xl employee-panel timesheet-admin">
        <div className="page-actions">
          <h1>{translate(resources.admin.timesheets.title)}</h1>
        </div>
        <div className="taa-actions-wrapper">
          <div className="date-action-wrapper">
            <span
              className="btn btn-icon"
              onClick={() => {
                setCurrentMonthFilter(
                  moment(currentMonthFilter).subtract({
                    month: 1,
                  }),
                );
                clearErrors('null');
              }}
            >
              <Icons.ArrowLeftMobile />
            </span>
            <span className="date-month">
              {formatSelectedMonthYear(
                currentMonthFilter,
                customerInfo?.languageCode ?? config.defaultLanguage ?? 'ro',
              )}
            </span>
            <span
              className="btn btn-icon"
              onClick={() => {
                setCurrentMonthFilter(
                  moment(currentMonthFilter).add({
                    month: 1,
                  }),
                );
                clearErrors('null');
              }}
            >
              <Icons.ArrowRightMobile />
            </span>
          </div>
          <div className="timesheet-admin-action">
            <Button
              className="btn btn-solid btn-sm"
              onClick={handleGenerateReport}
              loading={isGeneratingReport}
            >
              <span className="btn-icon">
                <Icons.DownloadFile />
              </span>
              <span className="btn-text">
                {translate(resources.admin.timesheets.actions.exportExcel)}
              </span>
            </Button>
          </div>
        </div>

        {/* Timesheet search*/}
        {!isMobile ? (
          <div className="timesheet-employee-search">
            <div>
              <div>
                <div className="timesheet-employee-search-column t-esi-s">
                  <Dropdown
                    value={companyFilter}
                    defaultValue={companyDefaultItem}
                    data={_.orderBy(
                      [...companyItems, companyDefaultItem],
                      'id',
                      'asc',
                    )}
                    dataItemKey="id"
                    textField="name"
                    onChange={onCampanyFilterChanged}
                  />
                </div>
                <div className="timesheet-employee-search-column t-esi-e">
                  <Dropdown
                    value={searchCrtFilter}
                    defaultValue={searchCrtItems[0]}
                    data={searchCrtItems}
                    dataItemKey="id"
                    textField="name"
                    onChange={onSearchCrtFilterChanged}
                  />
                  <Input
                    type="text"
                    value={searchFilter}
                    placeholder={translate(
                      resources.admin.timesheets.filters.searchPlaceholder,
                    )}
                    onChange={onSearchFilterChange}
                  />
                </div>

                <div className="timesheet-employee-search-column t-esi-s">
                  <Dropdown
                    value={isSubmittedFilter}
                    defaultValue={isSubmittedItems[0]}
                    data={isSubmittedItems}
                    dataItemKey="id"
                    textField="name"
                    onChange={onIsSubmittedFilterChanged}
                  />
                </div>

                <div className="timesheet-employee-search-column t-esi-s">
                  <Dropdown
                    value={isApprovedFilter}
                    defaultValue={isApprovedItems[0]}
                    data={isApprovedItems}
                    dataItemKey="id"
                    textField="name"
                    onChange={onIsApprovedFilterChanged}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="timesheet-employee-search-mobile">
            <div className="t-esi-e">
              <Dropdown
                value={searchCrtFilter}
                defaultValue={searchCrtItems[0]}
                data={searchCrtItems}
                dataItemKey="id"
                textField="name"
                onChange={onSearchCrtFilterChanged}
              />
              <Input
                type="text"
                value={searchFilter}
                placeholder={translate(
                  resources.admin.timesheets.filters.searchPlaceholder,
                )}
                onChange={onSearchFilterChange}
              />
            </div>
            <ExpandablePanel
              expandOn={'header'}
              arrow={false}
              title={
                <div>{translate(resources.admin.advancedSearchLabel)}</div>
              }
            >
              <div className="admin-employees-adv-search">
                <div className="t-esi-c">
                  <Dropdown
                    value={companyFilter}
                    defaultValue={companyDefaultItem}
                    data={_.orderBy(
                      [...companyItems, companyDefaultItem],
                      'id',
                      'asc',
                    )}
                    dataItemKey="id"
                    textField="name"
                    onChange={onCampanyFilterChanged}
                  />
                </div>
                <div className="t-esi-s">
                  <Dropdown
                    value={isSubmittedFilter}
                    defaultValue={isSubmittedItems[0]}
                    data={isSubmittedItems}
                    dataItemKey="id"
                    textField="name"
                    onChange={onIsSubmittedFilterChanged}
                  />
                </div>
                <div className="t-esi-s">
                  <Dropdown
                    value={isApprovedFilter}
                    defaultValue={isApprovedItems[0]}
                    data={isApprovedItems}
                    dataItemKey="id"
                    textField="name"
                    onChange={onIsApprovedFilterChanged}
                  />
                </div>
              </div>
            </ExpandablePanel>
          </div>
        )}

        <DataLoader
          getData={timesheetsAdminService.getTimesheets}
          dataState={state.employees.dataState}
          onDataReceived={onDataReceived}
          optionalQueryString={`selectedMonth=${state.employees.currentMonth.format(
            'YYYY-MM-DD',
          )}`}
          bypassRequestDataIfNeeded={reloadData}
        />
        <div>
          <Loader loading={!state.employees.data} loader={<LoadingSpinner />}>
            <div className="admin-grid timesheet-employees-grid">
              <Grid
                data={state.employees.data}
                pageable={{
                  pageSizes: config.grid.pageSizes,
                  pageSizeValue: state.employees.dataState.take,
                  info: false,
                  previousNext: true,
                }}
                {...state.employees.dataState}
                onDataStateChange={e => onDataStateChange(e)}
                total={state.employees.total}
                sortable={{ allowUnsort: true, mode: 'multiple' }}
                sort={state.employees.dataState.sort}
                columns={[
                  {
                    title: translate(
                      resources.admin.timesheets.grid.columns.select,
                    ),
                    sortable: false,
                    cell: selectCheckboxCell,
                    headerClassName: 'grid-check-cell',
                    headerCell: () => {
                      return (
                        <Checkbox
                          label={undefined}
                          onChange={() => toggleSelectAll()}
                          isChecked={
                            state.employees.data.length > 0 &&
                            selectedRows.size === state.employees.data.length
                          }
                        />
                      );
                    },
                  },
                  {
                    title: translate(
                      resources.admin.timesheets.grid.columns.name,
                    ),
                    field: 'employeeFullName',
                    sortable: true,
                    cell: employeeFullNameCell,
                  },
                  {
                    title: translate(
                      resources.admin.timesheets.grid.columns.eID,
                    ),
                    field: 'email',
                    sortable: false,
                    cell: emailCell,
                    headerClassName: 'grid-email',
                  },
                  {
                    title: translate(
                      resources.admin.timesheets.grid.columns.company,
                    ),
                    field: 'companyName',
                    sortable: false,
                    cell: companyNameCell,
                  },
                  {
                    title: translate(
                      resources.admin.timesheets.grid.columns.submitted,
                    ),
                    field: 'isSubmitted',
                    cell: SubmittedCell,
                    sortable: false,
                  },
                  {
                    title: translate(
                      resources.admin.timesheets.grid.columns.approved,
                    ),
                    field: 'isApproved',
                    cell: ApprovedCell,
                    sortable: false,
                  },
                  {
                    cell: ActionsCellCustomCell,
                    headerClassName: 'grid-actions-cell',
                  },
                ]}
              />
            </div>
          </Loader>
          {selectedRows.size > 0 && (
            <div className="employee-timesheet-sticky-footer">
              <div>
                <div className="mselect-count">
                  <span>
                    {translate(resources.admin.timesheets.stickyFooter.title, {
                      ['size']: selectedRows.size,
                    })}
                  </span>
                  <button className="btn-link" onClick={toggleSelectAll}>
                    {!isTablet ? (
                      <span className="btn-icon">
                        <Icons.ArrowButton />
                      </span>
                    ) : (
                      <div className="icon-select-all-mobile">
                        <Icons.Checklist />
                      </div>
                    )}

                    <span className="btn-text">
                      {selectedRows.size === state.employees.data.length
                        ? `${translate(
                            resources.admin.timesheets.stickyFooter
                              .deselectMessage,
                          )}`
                        : `${translate(
                            resources.admin.timesheets.stickyFooter
                              .selectMessage,
                          )}`}
                    </span>
                  </button>
                </div>

                <div className="mselect-actions">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setShowApproveDialog(true)}
                  >
                    <span className="btn-icon">
                      <Icons.CheckAlt />
                    </span>
                    <span className="btn-text">
                      {translate(
                        resources.admin.timesheets.stickyFooter.buttons.approve,
                      )}
                    </span>
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleReopen}
                  >
                    <span className="btn-icon">
                      <Icons.PadlockOpen />
                    </span>
                    <span className="btn-text">
                      {translate(
                        resources.admin.timesheets.stickyFooter.buttons.reopen,
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {showTimesheetSummaryDialog && (
            <TimesheetSummaryDialog
              employeeName={timesheetSummaryEmployeeName}
              employeeId={timesheetSummaryEmployeeId}
              currentMonth={currentMonthFilter}
              setShowTimesheetSummaryDialog={setShowTimesheetSummaryDialog}
            />
          )}
          {showApproveDialog && (
            <ApproveTimesheetDialog
              close={() => {
                setShowApproveDialog(false);
              }}
              approve={handleApprove}
              isApproving={isApproving}
            />
          )}
        </div>
      </div>
    </>
  );
}
